import React, { useContext, useEffect, useRef, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Form, FormSpy } from 'react-final-form';
import { Box } from '@mui/material';
import ClientProfileButtons from 'components/client/profile/ClientProfileButtons';
import ClientProfileHeader from 'components/client/profile/ClientProfileHeader';
import ClientProfileTabs from 'components/client/profile/ClientProfileTabs';
import PersonalTabPanel from 'components/client/profile/PersonalTabPanel';
import SecurityTabPanel from 'components/client/profile/SecurityTabPanel';
import SEOPageTitle from 'components/shared/SEOPageTitle';
import TabPanel from 'components/shared/tabs/TabPanel';
import requestStatus from 'common/constants/status';
import getDataToSave from 'common/form/dataToSave';
import { updateClientProfileInitialValues } from 'common/form/initial-values/update';
import { getFieldsToUpdate } from 'common/form/utils';
import validateEditClientProfileForm from 'common/validations/form/editClientProfile';
import routes from 'common/routes';
import AppBarContext from 'context/AppBarContext';
import authSlice from 'store/modules/auth';
import geoSlice from 'store/modules/geo';

function ClientProfile() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { t } = useTranslation(['routes']);
  const pageTitle = t('profile', { ns: 'routes' });
  const [initialValues, setInitialValues] = useState({});
  const [currentTab, setCurrentTab] = useState(0);
  const [selectedCountry, setSelectedCountry] = useState(null);
  const [selectedState, setSelectedState] = useState(null);
  const formRef = useRef(null);
  const { setAppBarButton, setAppBarTitle } = useContext(AppBarContext);

  const countries = useSelector((state) => state.geo.countries);
  const states = useSelector((state) => state.geo.states);
  const cities = useSelector((state) => state.geo.cities);
  const geoStatus = useSelector((state) => state.geo.status);
  const status = useSelector((state) => state.auth.status);
  const user = useSelector((state) => state.auth.user);

  const wasIncomplete = useRef(user?.profile_complete === false);

  useEffect(() => {
    if (user && Object.keys(user).length > 0) {
      const userData = updateClientProfileInitialValues(user);
      setInitialValues(userData);

      // Pre-cargar departamentos/ciudades del país/estado ya guardados (perfil con datos).
      if (userData.country?.label) {
        dispatch(geoSlice.actions.getStates(userData.country.label));
        setSelectedCountry(userData.country.label);
      }
      if (userData.state?.value) {
        dispatch(geoSlice.actions.getCities({ state: userData.state.value }));
        setSelectedState(userData.state.value);
      }
    }
  }, [user]);

  useEffect(() => {
    if (wasIncomplete.current && user?.profile_complete === true) {
      navigate(routes.clientDashboard.path, { replace: true });
    }
  }, [user?.profile_complete, navigate]);

  useEffect(() => {
    if (geoStatus !== requestStatus.loading && countries.length <= 0) {
      dispatch(geoSlice.actions.getCountries());
    }
  }, []);

  useEffect(() => {
    setAppBarButton(null);
    setAppBarTitle(null);
  }, [setAppBarButton, setAppBarTitle]);

  const onTabChange = (event, newValue) => {
    setCurrentTab(newValue);
  };

  const onUpdate = (values) => {
    const fieldsToUpdate = getFieldsToUpdate(values, initialValues);
    const dataToSave = getDataToSave(fieldsToUpdate, 'user');
    dispatch(authSlice.actions.updateMe({ id: user.id, input: dataToSave }));
  };

  // Cascada geo: al cambiar país carga departamentos (y resetea state/city); al cambiar
  // departamento carga ciudades (y resetea city). Mismo patrón que el form admin de cliente.
  const onFormChange = ({ dirtyFields, values }) => {
    const dirtyKeys = Object.keys(dirtyFields);

    if (values.country?.label && values.country.label !== selectedCountry) {
      setSelectedCountry(values.country.label);
      dispatch(geoSlice.actions.getStates(values.country.label));

      if ('country' in dirtyKeys) {
        setSelectedState(null);
        if (values.state) formRef.current.change('state', null);
        if (values.city) formRef.current.change('city', null);
      }
    }

    if (values.state?.value && values.state.value !== selectedState) {
      setSelectedState(values.state.value);
      dispatch(geoSlice.actions.getCities({ state: values.state.value }));

      if ('state' in dirtyKeys && values.city) formRef.current.change('city', null);
    }
  };

  return (
    <>
      <SEOPageTitle title={pageTitle} />
      <Box
        sx={{
          maxWidth: 980,
          mx: 'auto',
          p: { xs: 2.5, md: 4 },
          borderRadius: 4,
          backgroundColor: 'surface.content',
          border: '1px solid',
          borderColor: 'hairline.main',
          boxShadow: 'inset 0 1px 0 var(--otx-inner-highlight), var(--otx-card-shadow)'
        }}>
        <Form
          initialValues={initialValues}
          onSubmit={onUpdate}
          validate={validateEditClientProfileForm}
          render={({ form, handleSubmit, invalid, pristine, values }) => {
            formRef.current = form;

            return (
              <form onSubmit={handleSubmit}>
                <ClientProfileHeader />
                <ClientProfileTabs currentTab={currentTab} onTabChange={onTabChange} />
                <TabPanel current={currentTab} index={0}>
                  <PersonalTabPanel
                    countries={countries}
                    states={states}
                    cities={cities}
                    geoStatus={geoStatus}
                    values={values}
                  />
                </TabPanel>
                <TabPanel current={currentTab} index={1}>
                  <SecurityTabPanel />
                </TabPanel>
                <ClientProfileButtons invalid={invalid} pristine={pristine} status={status} />
                <FormSpy onChange={onFormChange} subscription={{ dirtyFields: true, values: true }} />
              </form>
            );
          }}
        />
      </Box>
    </>
  );
}

export default ClientProfile;
